// Appointment time zone helpers: an appointment is date + HH:MM in its own time_zone (one exact moment).
export const DEFAULT_TZ = 'America/Chicago';
export const TIME_ZONE_OPTIONS = [
  ['America/New_York', 'Eastern Time'], ['America/Chicago', 'Central Time'], ['America/Denver', 'Mountain Time'],
  ['America/Phoenix', 'Arizona Time'], ['America/Los_Angeles', 'Pacific Time'], ['America/Anchorage', 'Alaska Time'], ['Pacific/Honolulu', 'Hawaii Time'],
];

export const viewerTz = (user) => user?.preferences?.timezone || user?.timezone || DEFAULT_TZ;

const partsIn = (ts, tz) => {
  const p = {};
  new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    .formatToParts(new Date(ts)).forEach(({ type, value }) => { p[type] = value; });
  return p;
};
const offsetMs = (ts, tz) => {
  const p = partsIn(ts, tz);
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute) - Math.floor(ts / 60000) * 60000;
};

// Wall-clock date/time in a zone -> Date
export const zonedToDate = (date, hhmm, tz) => {
  if (!date || !hhmm) return null;
  const [y, m, d] = date.slice(0, 10).split('-').map(Number);
  const [h, mi] = hhmm.slice(0, 5).split(':').map(Number);
  if ([y, m, d, h, mi].some(Number.isNaN)) return null;
  const guess = Date.UTC(y, m - 1, d, h, mi);
  let ts = guess - offsetMs(guess, tz || DEFAULT_TZ);
  ts = guess - offsetMs(ts, tz || DEFAULT_TZ);
  return new Date(ts);
};

export const zoneAbbr = (dt, tz) => new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
  .formatToParts(dt).find(x => x.type === 'timeZoneName')?.value || '';

export const formatTime12 = (hhmm) => {
  if (!hhmm || !/^\d{1,2}:\d{2}/.test(hhmm)) return hhmm || '';
  const [h, m] = hhmm.split(':').map(Number);
  return `${(h + 11) % 12 + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

export const timeIn = (dt, tz, withZone = true) => {
  const t = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true }).format(dt);
  return withZone ? `${t} ${zoneAbbr(dt, tz)}` : t;
};

// Appointment shown in the viewer's zone: date/start_time/end_time become the viewer's wall clock
export const toViewerZone = (apt, tz) => {
  if (!apt || apt._tzConverted) return apt;
  const src = apt.time_zone || DEFAULT_TZ;
  const s = zonedToDate(apt.date, apt.start_time, src);
  if (!s) return { ...apt, _tzConverted: true };
  const e = zonedToDate(apt.date, apt.end_time, src);
  const p = partsIn(s.getTime(), tz);
  const hm = (dt) => { const q = partsIn(dt.getTime(), tz); return `${q.hour === '24' ? '00' : q.hour}:${q.minute}`; };
  return { ...apt, _tzConverted: true, date: `${p.year}-${p.month}-${p.day}`, start_time: hm(s), end_time: e ? hm(e) : apt.end_time,
    time_zone: tz, tz_abbr: zoneAbbr(s, tz), original_time_zone: src };
};

export const aptTimeLabel = (apt) => {
  if (!apt?.start_time) return '';
  const range = `${formatTime12(apt.start_time)}${apt.end_time ? ` - ${formatTime12(apt.end_time)}` : ''}`;
  return apt.tz_abbr ? `${range} ${apt.tz_abbr}` : range;
};

// "Participant sees: 10:00 AM EST" for a time picked by staff in their zone
export const participantSeesLabel = (date, hhmm, staffTz, participantTz) => {
  const dt = zonedToDate(date, hhmm, staffTz);
  if (!dt) return '';
  return timeIn(dt, participantTz || staffTz);
};

export const formatDateTimeLong = (input, tz) => {
  if (!input) return '';
  const dt = new Date(input);
  if (Number.isNaN(dt.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).format(dt);
};
